/**
 * 注意事項ページ
 *************************************************/
import { GraphQLResult } from '@aws-amplify/api';
import { AnswerByUserIdQuery } from 'API';
import { API, Auth, graphqlOperation } from 'aws-amplify';
import Footer from 'components/common/Footer';
import { deleteAnswer } from 'graphql/mutations';
import { answerByUserId } from 'graphql/queries';
import { startLoading, endLoading } from 'modules/commonModule';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Confirm, Container, Divider, Header, Icon, List } from 'semantic-ui-react';

type LocationState = {
  userId: string;
};
const NoticePage: FC = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation<LocationState>();
  const { userId } = location.state;
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);

  /**
   * 試験例題ページへ遷移
   */
  const actionExcute = useCallback(async () => {
    try {
      dispatch(startLoading());

      // ダイアログを閉じる
      setIsOpenDialog(false);

      const user = await Auth.currentAuthenticatedUser();
      const now = moment();

      if (user.attributes['custom:role'] !== 'tester') {
        await Auth.updateUserAttributes(user, {
          'custom:status': '1',
          'custom:startAt': now.format('YYYY-MM-DD HH:mm'),
        });
      }

      // 検証ユーザーの場合は、試験開始前に過去の解答データを削除しておく
      if (user.attributes['custom:role'] === 'tester') {
        const resAnswers = (await API.graphql(
          graphqlOperation(answerByUserId, { userId: user.username }),
        )) as GraphQLResult<AnswerByUserIdQuery>;
        const answers = resAnswers.data?.answerByUserId?.items;

        answers?.forEach((answer) => {
          if (answer) {
            API.graphql(graphqlOperation(deleteAnswer, { input: { id: answer.id } }));
          }
        });
      }

      // 例題の制限時間をセッションストレージに保持（次画面の初期値として利用）
      sessionStorage.setItem('time', '300000');

      // 試験例題ページへ遷移
      history.push('/example', {
        userId,
        categoryNo: 1,
      });
    } catch (e) {
      console.log(e);
    } finally {
      dispatch(endLoading());
    }
  }, [dispatch, history, userId]);

  useEffect(() => {
    (async () => {
      try {
        dispatch(startLoading());

        // 認証チェック
        await Auth.currentAuthenticatedUser();
      } catch (e) {
        console.log(e);
        // 認証できなかった場合はログイン画面へリダイレクト
        if (e === 'not authenticated') {
          history.push('/');
        }
      } finally {
        dispatch(endLoading());
      }
    })();
  }, [dispatch, history]);

  return (
    <>
      <Container text style={{ paddingTop: '80px', marginBottom: '70px' }}>
        <Header as="h1" dividing textAlign="center" style={{ paddingBottom: '20px', marginBottom: '60px' }}>
          受験前の注意事項
        </Header>

        <List>
          <List.Item style={{ marginBottom: '15px' }}>
            <List.Icon name="marker" />
            <List.Content>
              <List.Header>テストは1回のみ受験可能です。</List.Header>
            </List.Content>
          </List.Item>
          <List.Item style={{ marginBottom: '15px' }}>
            <List.Icon name="marker" />
            <List.Content>
              <List.Header>
                テストを開始すると、テスト終了まで中断はできません。（テスト時間の目安は90分です）
              </List.Header>
            </List.Content>
          </List.Item>
          <List.Item style={{ marginBottom: '15px' }}>
            <List.Icon name="marker" />
            <List.Content>
              <List.Header>計算が必要な問題があるため、計算用紙や筆記用具をご用意の上開始してください。</List.Header>
            </List.Content>
          </List.Item>
          <List.Item style={{ marginBottom: '15px' }}>
            <List.Icon name="marker" />
            <List.Content>
              <List.Header>
                各問題には制限時間があり、制限時間を過ぎると解答できなくなりますので、時間に注意して実施してください。
              </List.Header>
            </List.Content>
          </List.Item>
          <List.Item>
            <List.Icon name="marker" />
            <List.Content>
              <List.Header>
                何かわからないことやトラブル等が発生した場合は、以下まで連絡ください。
                <br />
                <br />
                <div style={{ marginBottom: '15px' }}>
                  <span style={{ lineHeight: '1.5' }}>
                    ■お問い合わせ
                    <br />
                    TEL：06-6348-2885
                    <br />
                    電話受付：平日9:00～17:00
                  </span>
                </div>
                <Button
                  primary
                  size="medium"
                  icon
                  labelPosition="right"
                  content={
                    <>
                      <a
                        href="https://www.freecraft.co.jp/contact/"
                        style={{ color: 'white' }}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        お問い合わせフォームへ
                      </a>
                      <Icon name="envelope" />
                    </>
                  }
                />
              </List.Header>
            </List.Content>
          </List.Item>
        </List>

        <Divider section />
        <Container textAlign="center">
          <Button primary size="huge" onClick={() => setIsOpenDialog(true)} data-cy="start-button">
            適性検査を開始する
            <Icon name="arrow right" />
          </Button>
          <Confirm
            header="試験を開始します"
            content={() => {
              // eslint-disable-next-line react/no-danger
              return (
                <p
                  style={{ padding: '20px' }}
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html:
                      '試験へ進むと試験終了まで終了できません。（目安は90分です）<br/>このまま進んでもよろしいですか？',
                  }}
                />
              );
            }}
            open={isOpenDialog}
            onCancel={() => setIsOpenDialog(false)}
            onConfirm={actionExcute}
            size="tiny"
            data-cy="confirm-dialog"
          />
        </Container>
      </Container>
      <Footer />
    </>
  );
};
export default NoticePage;
